<template>
  <div class="card-page">
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main class="main-content">
        <header class="page-header" :class="{ 'is-scrolled': isHeaderScrolled }">
          <button
            type="button"
            class="mobile-back-btn"
            @click="handleGoBack"
            aria-label="返回上一页"
          >
            <font-awesome-icon icon="chevron-left" />
          </button>
          <h1 class="main-title">
            TPR <span class="highlight">互动卡</span>
          </h1>
        </header>
        <div class="page-header-spacer" aria-hidden="true"></div>

        <nav class="category-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            type="button"
            class="tab-item"
            :class="{ active: activeTab === tab.id }"
            @click="selectTab(tab)"
          >
            <font-awesome-icon :icon="tab.icon" class="tab-icon" />
            <span>{{ tab.label }}</span>
          </button>
        </nav>

        <div class="cards-grid">
          <template v-if="showTprSkeleton">
            <article
              v-for="index in 8"
              :key="`tpr-skeleton-${index}`"
              class="word-card tpr-skeleton-card"
            >
              <div class="card-surface">
                <div class="card-video-wrap tpr-skeleton-media"></div>
              </div>
            </article>
          </template>

          <article
            v-for="(card, index) in showTprSkeleton ? [] : filteredCards"
            :key="card.id"
            class="word-card"
            :class="{ playing: currentPlayingIndex === index }"
          >
            <div
              class="card-surface"
              :aria-label="`播放第 ${index + 1} 个视频`"
              role="button"
              tabindex="0"
              @click="handleCardClick(card, index)"
              @keydown.enter.prevent="handleCardClick(card, index)"
              @keydown.space.prevent="handleCardClick(card, index)"
            >
              <div
                class="card-video-wrap"
                :style="{
                  background: card.bgColor,
                  '--tpr-video-aspect-ratio': getCardAspectRatio(card.id)
                }"
              >
                <video
                  :ref="(el) => setCardVideoRef(card.id, el)"
                  class="card-video"
                  :class="{ active: currentPlayingIndex === index }"
                  :src="card.videoUrl"
                  :poster="card.coverUrl"
                  playsinline
                  preload="auto"
                  controlslist="nodownload nofullscreen noremoteplayback"
                  disablepictureinpicture
                  @click.stop="handleVideoElementClick(card, index)"
                  @loadedmetadata="handleVideoMetadata(card.id, $event)"
                  @play="handleVideoPlay(card.id, index)"
                  @pause="handleVideoPause(card.id)"
                  @ended="handleVideoEnded(card.id)"
                ></video>

                <img
                  v-if="playingCardId !== card.id && card.coverUrl"
                  :src="card.coverUrl"
                  alt=""
                  class="card-video-cover"
                />

                <div
                  v-if="playingCardId !== card.id"
                  class="video-status"
                  aria-hidden="true"
                >
                  <font-awesome-icon icon="play" />
                </div>
              </div>
            </div>
          </article>

          <div v-if="false" class="empty-state">
            <font-awesome-icon icon="hand-paper" />
            <p>暂无 TPR 内容</p>
          </div>
        </div>
      </main>
    </div>

    <div class="dock-bar">
      <button class="dock-item dock-item-secondary" @click="goToFullscreen">
        <font-awesome-icon icon="expand" />
        <span>全屏模式</span>
      </button>

      <button class="dock-item main-btn dock-play-btn" @click="toggleAutoPlay">
        <font-awesome-icon :icon="isAutoPlaying ? 'pause' : 'play'" />
      </button>
    </div>

    <ParentPasswordModal ref="parentPasswordModalRef" />
    <LoginPromptModal v-model="isLoginPromptOpen" @confirm="goLogin" />
    <VipPromptModal
      v-model="isVipPromptOpen"
      description="成为会员后可观看完整内容"
      @confirm="goVip"
    />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import LoginPromptModal from '@/components/LoginPromptModal.vue'
import VipPromptModal from '@/components/VipPromptModal.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getTprList, type TprVO } from '@/api/course-tpr'
import { addHistory } from '@/api/history'
import { getAccessToken } from '@/config/axios/auth'

const isGuestUser = computed(() => getAccessToken() == null)
const isNonVipUser = computed(() =>
  !isGuestUser.value && localStorage.getItem('vipTag') === 'false'
)

const route = useRoute()
const router = useRouter()
const TPR_CONTENT_TYPE = 3

const activeTab = ref('body')
const activeNav = ref('')
const currentPlayingIndex = ref(-1)
const playingCardId = ref(null)
const isHeaderScrolled = ref(false)
const isAutoPlaying = ref(false)
const pausedIndex = ref(-1)
const parentPasswordModalRef = ref(null)
const isLoginPromptOpen = ref(false)
const isVipPromptOpen = ref(false)
let autoPlayTimer = null
const isLoading = ref(false)
const historyRecordedIds = new Set<number>()
const videoAspectRatios = ref<Record<string, string>>({})

const cardVideoRefs = new Map()

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const tabs = [
  { id: 'body', label: '身体指令', icon: 'hand-paper' },
  { id: 'daily', label: '日常生活', icon: 'home' },
  { id: 'emotion', label: '情绪感受', icon: 'smile' },
]

const queryParams = ref({
  category: tabs[0].label,
  stage: route.query.stage
})

const tprList = ref<TprVO[]>([])

const cardColors = [
  'rgba(255, 140, 66, 0.15)',
  'rgba(91, 143, 249, 0.15)',
  'rgba(76, 175, 80, 0.15)',
  'rgba(231, 76, 60, 0.15)',
  'rgba(255, 193, 7, 0.15)',
  'rgba(156, 39, 176, 0.15)',
  'rgba(0, 150, 136, 0.15)',
  'rgba(33, 150, 243, 0.15)'
]

const filteredCards = computed(() => {
  return tprList.value.map((item, index) => ({
    ...item,
    bgColor: cardColors[index % cardColors.length]
  }))
})

const showTprSkeleton = computed(() => isLoading.value && filteredCards.value.length === 0)

const selectTab = (tab) => {
  activeTab.value = tab.id
  queryParams.value.category = tab.label
  getList()
}

watch(activeTab, () => {
  stopAutoPlay()
  pauseAllVideos()
  currentPlayingIndex.value = -1
  playingCardId.value = null
  pausedIndex.value = -1
})

const setCardVideoRef = (cardId, el) => {
  if (el) {
    cardVideoRefs.set(cardId, el)
    return
  }

  cardVideoRefs.delete(cardId)
}

const getCardAspectRatio = (cardId) => {
  return videoAspectRatios.value[String(cardId)] || '4 / 5'
}

const handleVideoMetadata = (cardId, event: Event) => {
  const video = event.target as HTMLVideoElement | null
  if (!video?.videoWidth || !video?.videoHeight) {
    return
  }

  const cardKey = String(cardId)
  videoAspectRatios.value = {
    ...videoAspectRatios.value,
    [cardKey]: `${video.videoWidth} / ${video.videoHeight}`
  }
}

const openLoginPrompt = () => {
  isLoginPromptOpen.value = true
}

const goLogin = () => {
  isLoginPromptOpen.value = false
  router.push('/login')
}

const goVip = () => {
  isVipPromptOpen.value = false
  router.push('/vip')
}

const ensureCanPlayTpr = () => {
  if (isGuestUser.value) {
    openLoginPrompt()
    return false
  }

  if (isNonVipUser.value) {
    isVipPromptOpen.value = true
    return false
  }

  return true
}

const playVideo = async (video, restart = false) => {
  if (!video) return

  if (restart) {
    video.currentTime = 0
  }

  try {
    await video.play()
  } catch (error) {
    // Ignore blocked autoplay and keep the UI responsive.
  }
}

const pauseAllVideos = (exceptId = null) => {
  cardVideoRefs.forEach((video, cardId) => {
    if (!video || cardId === exceptId) return
    video.pause()
    video.currentTime = 0
  })
}

const playCardAtIndex = async (index, restart = true) => {
  if (!ensureCanPlayTpr()) {
    return
  }

  const card = filteredCards.value[index]
  if (!card) return

  const video = cardVideoRefs.get(card.id)
  if (!video) return

  currentPlayingIndex.value = index
  pausedIndex.value = index
  pauseAllVideos(card.id)
  await playVideo(video, restart)
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const handleCardClick = async (card, index) => {
  if (!ensureCanPlayTpr()) {
    return
  }

  const video = cardVideoRefs.get(card.id)
  if (!video) return

  if (currentPlayingIndex.value === index && !video.paused && !video.ended) {
    video.pause()
    return
  }

  await playCardAtIndex(index, currentPlayingIndex.value !== index)
}

const handleVideoElementClick = (card, index) => {
  const video = cardVideoRefs.get(card.id)
  if (!video) return

  if (currentPlayingIndex.value !== index) {
    handleCardClick(card, index)
  }
}

const handleVideoPlay = (cardId, index) => {
  if (!ensureCanPlayTpr()) {
    const video = cardVideoRefs.get(cardId)
    if (video) {
      video.pause()
      video.currentTime = 0
    }
    return
  }

  currentPlayingIndex.value = index
  playingCardId.value = cardId
  pauseAllVideos(cardId)
  if(!isGuestUser.value) {
    addPlayHistory(cardId)
  }
}

const addPlayHistory = (cardId) => {
  if (historyRecordedIds.has(cardId)) {
    return
  }

  historyRecordedIds.add(cardId)
  addHistory({
    contentType: TPR_CONTENT_TYPE,
    contentId: cardId
  }).catch(() => {
    historyRecordedIds.delete(cardId)
  })
}

const handleVideoPause = (cardId) => {
  if (playingCardId.value === cardId) {
    playingCardId.value = null
  }
}

const handleVideoEnded = (cardId) => {
  if (playingCardId.value === cardId) {
    playingCardId.value = null
  }
}

const handlePageScroll = () => {
  isHeaderScrolled.value = window.scrollY > 12
}

const getList = async () => {
  if (isLoading.value) {
    return
  }

  isLoading.value = true
  try {
    tprList.value = await getTprList(queryParams.value) || []
  } catch {
    tprList.value = []
  } finally {
    isLoading.value = false
  }
}

const goToFullscreen = () => {
  if (!ensureCanPlayTpr()) {
    return
  }

  router.push({
    path: '/tpr-fullscreen',
    query: {
      category: queryParams.value.category,
      stage: queryParams.value.stage
    }
  })
}

const toggleAutoPlay = () => {
  if (isAutoPlaying.value) {
    stopAutoPlay()
  } else {
    if (!ensureCanPlayTpr()) {
      return
    }

    startAutoPlay()
  }
}

const startAutoPlay = async () => {
  if (filteredCards.value.length === 0) return

  if (pausedIndex.value >= 0) {
    currentPlayingIndex.value = pausedIndex.value
  } else {
    currentPlayingIndex.value = 0
  }

  await playCardAtIndex(currentPlayingIndex.value)
  isAutoPlaying.value = true

  autoPlayTimer = window.setInterval(async () => {
    const nextIndex = (currentPlayingIndex.value + 1) % filteredCards.value.length
    await playCardAtIndex(nextIndex)
  }, 3000)
}

const stopAutoPlay = () => {
  isAutoPlaying.value = false
  pausedIndex.value = currentPlayingIndex.value

  if (autoPlayTimer) {
    window.clearInterval(autoPlayTimer)
    autoPlayTimer = null
  }
}

onMounted(() => {
  getList()
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
  stopAutoPlay()
  pauseAllVideos()
})
</script>

<style scoped>
:root {
  --primary: #ff8c42;
  --bg: #f9f7f2;
  --text: #2d3436;
}

.card-page {
  min-height: 100vh;
  background: var(--bg);
  position: relative;
  overflow: hidden;
  padding-bottom: 0;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-gradient {
  position: fixed;
  inset: 0;
  background: linear-gradient(135deg, #fff8f0 0%, #f9f7f2 50%, #f5f0e8 100%);
  z-index: -3;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.6;
  z-index: -2;
  animation: float 8s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #ffe4d4, #ffd4c4);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #e8f4ff, #d4e8ff);
  bottom: -100px;
  left: -100px;
  animation-delay: -3s;
}

.orb-3 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, #ffeaa8, #ffd88a);
  top: 50%;
  left: 30%;
  transform: translateY(-50%);
  animation-delay: -5s;
  opacity: 0.4;
}

@keyframes float {
  0%,
  100% {
    transform: translate(0, 0) scale(1);
  }

  33% {
    transform: translate(30px, -30px) scale(1.05);
  }

  66% {
    transform: translate(-20px, 20px) scale(0.95);
  }
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
  height: 100vh;
  overflow: hidden;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 0;
  z-index: 2;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.main-content {
  flex: 1;
  min-width: 0;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  height: 100vh;
  padding: 60px 40px 118px;
  position: relative;
  z-index: 1;
  overflow: hidden;
}

.page-header {
  position: relative;
  text-align: center;
  margin-bottom: 50px;
}

.page-header-spacer {
  display: none;
}

.mobile-back-btn {
  display: none;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.main-title {
  font-size: 64px;
  font-weight: 900;
  color: var(--text);
  letter-spacing: 4px;
  line-height: 1;
}

.main-title .highlight {
  color: var(--primary);
  position: relative;
  display: inline-block;
}

.main-title .highlight::after {
  content: '';
  position: absolute;
  bottom: 8px;
  left: 0;
  width: 100%;
  height: 12px;
  background: linear-gradient(135deg, rgba(255, 140, 66, 0.3), rgba(255, 107, 53, 0.2));
  border-radius: 6px;
  z-index: -1;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 50px;
  flex-wrap: wrap;
}

.tab-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 32px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 50px;
  color: #666;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.tab-item span {
  white-space: nowrap;
}

.tab-item:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.tab-item.active {
  color: #fff;
  background: #ff8c42;
  border-color: #ff8c42;
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.35);
}

.tab-icon {
  font-size: 18px;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 32px;
  max-height: calc(100vh - 300px);
  overflow-x: hidden;
  overflow-y: auto;
  align-content: start;
}

.word-card {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 38px;
  transition: all 0.35s ease;
  overflow: hidden;
}

.word-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 28px 50px rgba(0, 0, 0, 0.1);
}

.word-card.playing {
  transform: scale(1.02);
  border-color: #ff8c42;
  box-shadow: 0 20px 50px rgba(255, 140, 66, 0.28);
}

.card-surface {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  display: block;
  cursor: pointer;
}

.card-video-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 4 / 5;
  overflow: hidden;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.12);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.tpr-skeleton-card {
  pointer-events: none;
}

.tpr-skeleton-media {
  background: linear-gradient(
    90deg,
    rgba(237, 231, 224, 0.78) 0%,
    rgba(255, 255, 255, 0.88) 45%,
    rgba(237, 231, 224, 0.78) 100%
  );
  background-size: 220% 100%;
  animation: tprSkeletonShimmer 1.25s ease-in-out infinite;
  box-shadow: none;
}

@keyframes tprSkeletonShimmer {
  0% {
    background-position: 120% 0;
  }
  100% {
    background-position: -120% 0;
  }
}

.word-card:hover .card-video-wrap,
.word-card.playing .card-video-wrap {
  transform: scale(1.02);
}

.card-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.5);
}

.card-video-cover {
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  pointer-events: none;
}

.card-video::-webkit-media-controls {
  display: none !important;
}

.card-video::-webkit-media-controls-enclosure {
  display: none !important;
}

.video-status {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.55);
  color: #fff;
  font-size: 22px;
  pointer-events: none;
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.18);
}

.dock-bar {
  position: fixed;
  bottom: 15px;
  left: calc(280px + (100vw - 280px) / 2);
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 8px 12px;
  width: auto;
  min-width: 250px;
  max-width: calc(100% - 32px);
  height: auto;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.9);
  border-radius: 9999px;
  z-index: 9999;
  box-shadow: 0 18px 40px rgba(95, 80, 68, 0.16);
}

.dock-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 18px 24px;
  background: transparent;
  border: none;
  border-radius: 20px;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.dock-item svg {
  font-size: 22px;
}

.dock-item:hover {
  background: rgba(0, 0, 0, 0.05);
  color: var(--text);
}

.dock-item.active {
  color: var(--primary);
  background: rgba(255, 140, 66, 0.1);
}

.dock-item.main-btn {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #ff8c42, #ff6b35);
  color: #fff;
  border-radius: 50%;
  padding: 0;
  margin: 0 8px;
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.4);
  font-size: 22px;
  justify-content: center;
  flex: 0 0 auto;
}

.dock-item.dock-item-secondary {
  flex-direction: row;
  gap: 10px;
  min-width: 0;
  height: 52px;
  padding: 0 18px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.56);
  border: 1px solid rgba(255, 255, 255, 0.82);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.dock-item.dock-item-secondary span {
  white-space: nowrap;
}

.dock-item.dock-play-btn {
  margin: 0;
}

.dock-item.main-btn:hover {
  transform: scale(1.08);
  box-shadow: 0 12px 40px rgba(255, 140, 66, 0.5);
}

.dock-item.main-btn:active {
  transform: scale(0.95);
}

@media (max-width: 1200px) {
  .sidebar {
    width: 224px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
    font-size: 20px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }

  .dock-bar {
    left: calc(224px + (100vw - 224px) / 2);
  }

  .cards-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .main-title {
    font-size: 52px;
  }
}

@media (max-width: 992px) {
  .sidebar {
    width: 208px;
  }

  .dock-bar {
    left: calc(208px + (100vw - 208px) / 2);
  }

  .cards-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 24px;
  }

  .main-content {
    padding: 40px 24px 120px;
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .card-page {
    overflow-x: hidden;
    overflow-y: auto;
    padding-bottom: 100px;
  }

  .content-wrapper {
    display: block;
    min-height: 100vh;
    height: auto;
    overflow: visible;
  }

  .sidebar {
    display: none;
  }

  .main-content {
    height: auto;
    overflow: visible;
    padding: 30px 16px 120px;
  }

  .page-header {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
    margin-bottom: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .page-header.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .page-header-spacer {
    display: block;
    height: 50px;
  }

  .mobile-back-btn {
    display: inline-flex;
    left: 16px;
  }

  .main-title {
    font-size: 28px;
  }

  .category-tabs {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 8px;
    margin-bottom: 28px;
  }

  .cards-grid {
    max-height: none;
    overflow: visible;
  }

  .tab-item {
    justify-content: center;
    min-width: 0;
    padding: 12px 10px;
    font-size: 12px;
  }

  .tab-item span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tab-icon {
    font-size: 14px;
  }

  .word-card {
    border-radius: 28px;
  }

  .card-video-wrap {
    aspect-ratio: var(--tpr-video-aspect-ratio, 4 / 5);
  }

  .card-video,
  .card-video-cover {
    object-fit: contain;
  }

  .dock-bar {
    left: 50%;
    padding: 10px 12px 10px 14px;
    gap: 10px;
    bottom: 20px;
  }

  .dock-item {
    padding: 10px 12px;
    font-size: 10px;
  }

  .dock-item.dock-item-secondary {
    height: 52px;
    padding: 0 16px;
  }

  .dock-item.dock-item-secondary span {
    display: block;
    font-size: 11px;
  }

  .dock-item.main-btn {
    width: 52px;
    height: 52px;
    margin: 0 4px;
    font-size: 18px;
  }
}

@media (max-width: 576px) {
  .cards-grid {
    gap: 16px;
  }

  .dock-bar {
    min-width: 0;
    max-width: calc(100% - 24px);
    bottom: 15px;
    gap: 8px;
    padding: 8px 10px 8px 12px;
    border-radius: 40px;
  }

  .dock-item {
    padding: 6px 4px;
    font-size: 10px;
  }

  .dock-item svg {
    font-size: 20px;
  }

  .dock-item.dock-item-secondary {
    height: 48px;
    padding: 0 14px;
  }

  .dock-item.dock-item-secondary span {
    display: block;
    font-size: 9px;
    margin-top: 0;
  }

  .dock-item.main-btn {
    width: 55px;
    height: 55px;
    margin: 0 4px;
    font-size: 18px;
    flex: 0 0 auto;
  }

  .tab-item {
    gap: 6px;
    padding: 11px 8px;
    font-size: 11px;
  }

  .tab-icon {
    font-size: 12px;
  }

  .video-status {
    width: 54px;
    height: 54px;
    font-size: 18px;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .orb {
    display: none;
  }

  .mobile-back-btn,
  .filter-chip,
  .tpr-card,
  .modal-content {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .nav-item,
  .filter-chip,
  .tpr-card,
  .modal-content,
  .modal-close {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
